import { Box, Stack, Typography } from '@mui/material';
import { Node as ReactFlowNode, NodeProps } from '@xyflow/react';
import { useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Tile, TileVariant } from '@/components/diagrams/components/Tile';
import { SemanticTileKind } from '@/components/diagrams/components/Tile/Tile.utils';
import { useCursorStyles } from '@/components/diagrams/FlowChart/hooks/useCursorStyles';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { TypographyClamped } from '@/components/typography/TypographyClamped';

import { useFamilyTreeContext } from '../contexts';
import { useFamilyTreeSlots } from '../contexts/familyTreeSlots.context';
import { PERSON_NODE_HEIGHT } from '../utils/elkLayout.constants';
import { calculateAge, getStateName } from '../utils/personInfo.utils';

const useLineStyles = makeStyles()((theme) => {
  return {
    // eslint-disable-next-line tss-unused-classes/unused-classes
    lineOne: { marginBottom: theme.spacing(0.5), lineHeight: 'unset' },
    // eslint-disable-next-line tss-unused-classes/unused-classes
    lineTwo: { marginBottom: theme.spacing(0.5), lineHeight: 'unset' },
    // eslint-disable-next-line tss-unused-classes/unused-classes
    lineThree: { marginBottom: theme.spacing(0.5), lineHeight: 'unset' },
  };
});

export interface PersonData {
  id: string;
  name: string;
  isPrimary: boolean;
  personId: string;
  generation?: number;
  dateOfBirth?: Date | null;
  dateOfDeath?: Date | null;
  stateCode?: string | null;
  isDeceased?: boolean;
  notes?: string | null;
  isSynthetic?: boolean;
}

export type PersonTileProps = NodeProps<
  ReactFlowNode<{
    person: PersonData;
  }>
>;

export function PersonTileBase({
  data,
  dragging,
  selected,
  ...tileProps
}: PersonTileProps) {
  const { person } = data;

  const { state } = useFamilyTreeContext();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const onMouseEnter = () => {
    if (showHiddenTooltip) {
      setTooltipOpen(true);
    }
  };

  const onMouseLeave = () => {
    setTooltipOpen(false);
  };

  const isSelectedFromState = state.selectedPersonId === person.personId;
  const isSelected = selected || isSelectedFromState;

  const { cursorClassName } = useCursorStyles(person.id);
  const { classes: lineClasses } = useLineStyles();

  const { visualizationConfig, nodeConfigs } = state;

  // Get state name if needed
  const stateName = visualizationConfig.showStateOfResidence
    ? getStateName(person.stateCode)
    : null;

  // Build lineTwo with state name if needed
  const lineTwo = useMemo(
    () =>
      stateName ? (
        <Typography
          variant="body-xs"
          sx={{
            textShadow: 'none',
          }}
        >
          {stateName}
        </Typography>
      ) : undefined,
    [stateName]
  );

  // Calculate age if needed
  const age = useMemo(() => {
    if (!visualizationConfig.showAge) {
      return null;
    }

    if (person.isDeceased) {
      return person.dateOfDeath?.getFullYear() ?? null;
    }

    return calculateAge(person.dateOfBirth);
  }, [person, visualizationConfig.showAge]);

  // Build lineThree with deceased badge or age
  // If deceased and age is shown, display "Deceased (YYYY)"
  // If living and age is shown, display "Age XX"
  const showDeceasedBadge = person.isDeceased === true;

  const lineThree = useMemo(() => {
    if (showDeceasedBadge) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          height="22px"
          sx={{
            textShadow: 'none',
            p: '1px', // Account for badge outline to prevent clipping from parent overflow:hidden
          }}
        >
          <Badge
            variant={BadgeVariants.SecondaryFilled}
            display={age !== null ? `Deceased (${age})` : 'Deceased'}
          />
        </Stack>
      );
    }

    // For living people, show age if available
    if (age !== null && visualizationConfig.showAge) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          height="22px"
          sx={{
            textShadow: 'none',
            p: '1px', // Account for badge outline to prevent clipping from parent overflow:hidden
          }}
        >
          <Badge
            variant={BadgeVariants.SecondaryOutline}
            display={`Age ${age}`}
          />
        </Stack>
      );
    }

    return undefined;
  }, [age, showDeceasedBadge, visualizationConfig.showAge]);

  const numLinesForNotes = useMemo(() => {
    if (lineTwo && lineThree) {
      return 2;
    }

    if (lineTwo || lineThree) {
      return 4;
    }

    return 6;
  }, [lineThree, lineTwo]);

  const configForNode = nodeConfigs[person.personId];

  const showNotes =
    (configForNode?.showNotes || visualizationConfig.showNotes) && person.notes;

  // Determine if the node is hidden
  const isNodeHidden = configForNode?.showTile === false;

  // The "click Settings to hide" hint points at the on-site/trowser editor
  // chrome, which the presentation slide doesn't have — so suppress it there.
  const showHiddenTooltip = isNodeHidden && !state.presentationMode;

  // Exclude height from tileProps to allow dynamic height based on content
  // React Flow automatically sets a measured height, but we want the tile to size based on its content
  const { height: _height, ...restTileProps } = tileProps;

  // Synthetic nodes should be completely invisible and non-interactive
  // but still occupy space in the layout for proper positioning
  if (person.isSynthetic) {
    return (
      <div
        data-person-id={person.personId}
        style={{
          width: tileProps.width,
          height: PERSON_NODE_HEIGHT,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
    );
  }

  const tileElement = (
    <Box>
      <Tile
        lineOne={person.name}
        lineTwo={lineTwo}
        lineThree={lineThree}
        notes={
          showNotes ? (
            <TypographyClamped
              lines={numLinesForNotes}
              variant="body-xs"
              tooltipProps={{
                title: person.notes,
              }}
            >
              {person.notes}
            </TypographyClamped>
          ) : undefined
        }
        classes={{
          root: cursorClassName,
          lineOne: lineClasses.lineOne,
          lineTwo: lineClasses.lineTwo,
          lineThree: lineClasses.lineThree,
        }}
        variant={person.isPrimary ? TileVariant.Primary : TileVariant.Secondary}
        isSelected={dragging || !!isSelected}
        isIconVariant={true}
        kind={isNodeHidden ? SemanticTileKind.Hidden : SemanticTileKind.Person}
        {...restTileProps}
      />
    </Box>
  );

  return (
    <div
      data-person-id={person.personId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {showHiddenTooltip ? (
        <Tooltip
          open={tooltipOpen}
          placement="top"
          title="To hide this individual from the diagram, click the Settings button in the upper left"
        >
          {tileElement}
        </Tooltip>
      ) : (
        tileElement
      )}
    </div>
  );
}

/** Slot-aware tile; if customizing, wrap PersonTileBase. */
export function PersonTile(props: PersonTileProps) {
  const { PersonTile: SlotPersonTile } = useFamilyTreeSlots();
  const Component = SlotPersonTile ?? PersonTileBase;
  return <Component {...props} />;
}
